import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useEffect } from "react";
import { getDocumentsData } from "../features/Documents/redux/eventOrganiserThunks";
import { motion, AnimatePresence } from "framer-motion";
import { FileText, ArrowRight } from "lucide-react";
import "./DashboardDocuments.css";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12, delayChildren: 0.1 },
  },
};

const categoryVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 80, damping: 18 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, scale: 0.9, y: 20 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { type: "spring", stiffness: 100, damping: 16 },
  },
};

const shimmerVariants = {
  initial: { backgroundPosition: "-200% 0" },
  animate: {
    backgroundPosition: "200% 0",
    transition: { repeat: Infinity, duration: 1.5, ease: "linear" },
  },
};

const SkeletonLoader = () => (
  <div className="dd-container">
    {[1, 2].map((i) => (
      <div key={i} className="dd-category">
        <motion.div
          className="dd-skeleton dd-skeleton-title"
          variants={shimmerVariants}
          initial="initial"
          animate="animate"
        />
        <div className="dd-cards-grid">
          {[1, 2, 3, 4].map((j) => (
            <motion.div
              key={j}
              className="dd-skeleton dd-skeleton-card"
              variants={shimmerVariants}
              initial="initial"
              animate="animate"
            />
          ))}
        </div>
      </div>
    ))}
  </div>
);

const DashboardDocuments = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { documentList = [], loading } = useSelector(
    (store) => store.eventOrganiser,
  );

  useEffect(() => {
    dispatch(getDocumentsData({ type: "propertyDoc" }));
  }, [dispatch]);

  const handleDocumentClick = (doc) => {
    navigate("/docFormTestRunNew?formId=" + doc.id, {
      state: {
        nameTitle: doc?.name_title,
        languageId: doc.languageId,
      },
    });
  };

  if (loading) {
    return <SkeletonLoader />;
  }

  return (
    <AnimatePresence>
      <motion.div
        className="dd-container"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {documentList.length === 0 ? (
          <motion.div
            className="dd-empty"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
          >
            <FileText size={48} strokeWidth={1.2} />
            <p>No documents found</p>
          </motion.div>
        ) : (
          documentList.map((category) => (
            <motion.div
              key={category.categoryId}
              className="dd-category"
              variants={categoryVariants}
            >
              <div className="dd-category-header">
                <h3 className="dd-category-name">{category.categoryName}</h3>
                {/* {category.forms && category.forms.length > 5 && (
                  <button className="dd-view-all">
                    View All hi
                    <ArrowRight size={14} />
                  </button>
                )} */}
              </div>

              {category.forms && category.forms.length > 0 ? (
                <motion.div
                  className="dd-cards-grid"
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                >
                  {/* slice(0, 5) */}
                  {category.forms.map((doc, index) => (
                    <motion.div
                      key={doc.id}
                      className="dd-card"
                      variants={cardVariants}
                      whileHover={{
                        y: -6,
                        scale: 1.02,
                        transition: {
                          type: "spring",
                          stiffness: 300,
                          damping: 20,
                        },
                      }}
                      whileTap={{ scale: 0.96 }}
                      onClick={() => handleDocumentClick(doc)}
                    >
                      <div className="dd-card-icon">
                        <FileText size={20} />
                      </div>
                      <span className="dd-card-title">
                        Create {doc.name_title}
                      </span>
                      <div className="dd-card-arrow">
                        <ArrowRight size={16} />
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
              ) : (
                <p className="dd-no-docs">No documents available</p>
              )}
            </motion.div>
          ))
        )}
      </motion.div>
    </AnimatePresence>
  );
};

export default DashboardDocuments;
